import httpCommon from "./http-common";

// Get all warehouses
const getAllWarehouses = (businessId: number) => {
  return httpCommon.get(`/v1/get/warehouses/${businessId}`);
};

// Get warehouse by ID
const getWarehouse = (warehouseId: string) => {
  return httpCommon.get(`/v1/get/warehouse/${warehouseId}`);
};

// Create warehouse
const createWarehouse = (businessId: number, data: any) => {
  return httpCommon.post(`/v1/add/warehouse/${businessId}`, data);
};

// Update warehouse
const updateWarehouse = (warehouseId: string, data: any) => {
  return httpCommon.put(`/v1/update/warehouse/${warehouseId}`, data);
};

// Delete warehouse
const deleteWarehouse = (warehouseId: string) => {
  return httpCommon.delete(`/v1/delete/warehouse/${warehouseId}`);
};

// Get warehouse stock summary
const getWarehouseStockSummary = (warehouseId: string) => {
  return httpCommon.get(`/v1/get/warehouse/stock-summary/${warehouseId}`);
};

// Get warehouse stock by item category
const getWarehouseStockByCategory = (warehouseId: string, categoryId?: string) => {
  const categoryParam = categoryId ? `?categoryId=${categoryId}` : '';
  return httpCommon.get(`/v1/get/warehouse/stock-by-category/${warehouseId}${categoryParam}`);
};

// Transfer stock between warehouses
const createStockTransfer = (businessId: number, data: any) => {
  return httpCommon.post(`/v1/add/stock-transfer/${businessId}`, data);
};

// Get stock transfers
const getAllStockTransfers = (businessId: number, warehouseId?: string, skip = 0, limit = 0) => {
  let queryParams = `?skip=${skip}&limit=${limit}`;
  if (warehouseId) queryParams += `&warehouseId=${warehouseId}`;
  return httpCommon.get(`/v1/get/stock-transfers/${businessId}${queryParams}`);
};

// Get stock transfer by ID
const getStockTransfer = (transferId: string) => {
  return httpCommon.get(`/v1/get/stock-transfer/${transferId}`);
};

// Update stock transfer status
const updateStockTransferStatus = (transferId: string, status: string) => {
  return httpCommon.put(`/v1/update/stock-transfer/status/${transferId}`, { status });
};

// Approve stock transfer
const approveStockTransfer = (transferId: string) => {
  return httpCommon.post(`/v1/approve/stock-transfer/${transferId}`);
};

// Receive stock transfer
const receiveStockTransfer = (transferId: string, data: any) => {
  return httpCommon.post(`/v1/receive/stock-transfer/${transferId}`, data);
};

// Get warehouse capacity utilization
const getWarehouseCapacity = (warehouseId: string) => {
  return httpCommon.get(`/v1/get/warehouse/capacity/${warehouseId}`);
};

// Get warehouse performance metrics
const getWarehousePerformance = (warehouseId: string, startDate?: string, endDate?: string) => {
  let queryParams = '';
  if (startDate && endDate) {
    queryParams = `?startDate=${startDate}&endDate=${endDate}`;
  }
  return httpCommon.get(`/v1/get/warehouse/performance/${warehouseId}${queryParams}`);
};

// Get warehouse locations
const getWarehouseLocations = (warehouseId: string) => {
  return httpCommon.get(`/v1/get/warehouse-locations/${warehouseId}`);
};

// Create warehouse location
const createWarehouseLocation = (warehouseId: string, data: any) => {
  return httpCommon.post(`/v1/add/warehouse-location/${warehouseId}`, data);
};

// Update warehouse location
const updateWarehouseLocation = (locationId: string, data: any) => {
  return httpCommon.put(`/v1/update/warehouse-location/${locationId}`, data);
};

// Delete warehouse location
const deleteWarehouseLocation = (locationId: string) => {
  return httpCommon.delete(`/v1/delete/warehouse-location/${locationId}`);
};

// Get inventory stock by warehouse location
const getInventoryStockByLocation = (warehouseLocationId: string) => {
  return httpCommon.get(`/v1/get/inventory-stock/location/${warehouseLocationId}`);
};

// Assign/Move inventory to warehouse location
const assignInventoryToLocation = (data: any) => {
  return httpCommon.post(`/v1/add/inventory-assignment`, data);
};

// Move inventory between warehouse locations
const moveInventoryBetweenLocations = (data: any) => {
  return httpCommon.post(`/v1/add/inventory-movement`, data);
};

// Get inventory movements for a warehouse location
const getInventoryMovementsByLocation = (warehouseLocationId: string, skip = 0, limit = 50) => {
  return httpCommon.get(`/v1/get/inventory-movements/location/${warehouseLocationId}?skip=${skip}&limit=${limit}`);
};

// Get cycle count schedules
const getCycleCountSchedules = (warehouseId: string) => {
  return httpCommon.get(`/v1/get/warehouse/cycle-counts/${warehouseId}`);
};

// Create cycle count
const createCycleCount = (warehouseId: string, data: any) => {
  return httpCommon.post(`/v1/add/warehouse/cycle-count/${warehouseId}`, data);
};

// Update cycle count results
const updateCycleCountResults = (cycleCountId: string, data: any) => {
  return httpCommon.put(`/v1/update/warehouse/cycle-count/${cycleCountId}`, data);
};

const WarehouseService = {
  // Warehouse Management
  getAllWarehouses,
  getWarehouse,
  createWarehouse,
  updateWarehouse,
  deleteWarehouse,

  // Stock Management
  getWarehouseStockSummary,
  getWarehouseStockByCategory,

  // Stock Transfers
  createStockTransfer,
  getAllStockTransfers,
  getStockTransfer,
  updateStockTransferStatus,
  approveStockTransfer,
  receiveStockTransfer,

  // Analytics & Performance
  getWarehouseCapacity,
  getWarehousePerformance,

  // Warehouse Locations
  getWarehouseLocations,
  createWarehouseLocation,
  updateWarehouseLocation,
  deleteWarehouseLocation,

  // Inventory Location Management
  getInventoryStockByLocation,
  assignInventoryToLocation,
  moveInventoryBetweenLocations,
  getInventoryMovementsByLocation,

  // Cycle Counting
  getCycleCountSchedules,
  createCycleCount,
  updateCycleCountResults
};

export default WarehouseService; 